
import isCaptureImage from '@/lib/isCaptureImage';
import { loadVideoThumbnail } from '@/lib/video';
import MediaPreviewItem from './MediaPreviewItem';
import { DirectUpload } from 'activestorage';
import { mapState, mapGetters } from 'vuex';

const imageFileToPreviewUrl = async file => {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
};

const videoFileToPreviewUrl = async file => {
  return new Promise(resolve => {
    // https://issues.chromium.org/issues/40123366 <- 안드로이드 웹뷰 엔진인 크로미움의 특정 버전에서 input으로부터의 file read가 제대로 동작하지 않는다는 이슈
    // HACK - ArrayBuffer를 사용해 file의 복제본을 만들어서 file read를 하면 동작한다.
    file.arrayBuffer().then(buffer => {
      const copiedFile = new File([buffer], file.name, { type: file.type });
      const url = loadVideoThumbnail(copiedFile);
      resolve(url);
    });
  });
};

export default {
  i18nComponentKey: 'ReviewsNew.components.MediaField',
  name: 'MediaField',
  components: { MediaPreviewItem },
  props: {
    media: { type: Array, default: () => [] },
    enableAiClothingDetection: { type: Boolean, default: false }
  },
  data() {
    return {
      items: this.media.map(e => ({ key: Symbol(), ...e }))
    };
  },
  computed: {
    maxMediaCount: () => 4,
    ...mapState(['api']),
    ...mapState('window', ['windowService']),
    ...mapGetters('reviewSettings', ['isVideoUploadable']),
    mediaPayload() {
      return this.items.map(item => {
        const itemPayload = {
          mediaType: item.mediaType,
          mediaStatus: item.mediaStatus,
          previewUrl: item.previewUrl
        };

        if (item.mediaType === 'image') {
          itemPayload.url = item.url;
          itemPayload.file = item.file;
          itemPayload.aiClothingDetected = item.aiClothingDetected;
        } else if (item.mediaType === 'video') {
          itemPayload.id = item.id;
        }

        return itemPayload;
      });
    }
  },
  watch: {
    mediaPayload() {
      this.$emit('update:media', this.mediaPayload);
    }
  },
  methods: {
    async onChangeFile(event) {
      const selected = Array.from(event.target.files);
      const captures = await Promise.all(
        selected.map(
          file => file.type.startsWith('image/') && isCaptureImage(file)
        )
      );
      const attachable = selected.filter((_, index) => !captures[index]);

      const remainingSlots = this.maxMediaCount - this.items.length;
      const captureExcluded = attachable.length < selected.length;
      const countExceeded = remainingSlots < attachable.length;

      if (captureExcluded && countExceeded) {
        this.windowService.alert(
          this.$t('capture_excluded_and_media_count_exceeded', [remainingSlots])
        );
      } else if (captureExcluded) {
        this.windowService.alert(this.$t('capture_excluded'));
      } else if (countExceeded) {
        this.windowService.alert(
          this.$t('media_count_exceeded', [this.maxMediaCount, remainingSlots])
        );
      }

      for (const file of attachable.slice(0, remainingSlots)) {
        if (file.type.startsWith('image/')) this.attachImage(file);
        else if (this.isVideoUploadable) this.attachVideo(file);
      }

      this.$refs.mediaInput.value = '';
    },
    async attachImage(file) {
      const previewUrl = await imageFileToPreviewUrl(file);
      const item = {
        key: Symbol(),
        mediaType: 'image',
        mediaStatus: 'ready',
        previewUrl,
        url: '',
        file,
        aiClothingDetected: null
      };
      this.items.push(item);

      if (this.enableAiClothingDetection) {
        try {
          this.$set(item, 'mediaStatus', 'analyzing');
          const formData = new FormData();
          formData.append('image', file);
          const { data } = await this.api.analyzeAiClothingImage(formData);
          this.$set(item, 'aiClothingDetected', data.result);
        } finally {
          this.$set(item, 'mediaStatus', 'ready');
        }
      }
    },
    async attachVideo(file) {
      const previewUrl = await videoFileToPreviewUrl(file);
      const item = {
        key: Symbol(),
        mediaType: 'video',
        mediaStatus: 'uploading',
        previewUrl,
        file,
        progress: 0
      };
      this.items.push(item);
      try {
        const blobId = await this.uploadVideo(file, event => {
          this.$set(item, 'progress', event.loaded / event.total);
        });

        this.$set(item, 'mediaStatus', 'creating');
        const { data } = await this.api.createVideo(blobId);
        this.$set(item, 'id', data.id);
        this.$set(item, 'mediaStatus', 'ready');
      } catch (error) {
        this.$set(item, 'mediaStatus', 'error');
      }
    },
    uploadVideo(file, onProgress) {
      return new Promise((resolve, reject) => {
        const directUpload = new DirectUpload(
          file,
          this.api.getVideoDirectUploadUrl(),
          {
            directUploadWillStoreFileWithXHR: request => {
              request.upload.addEventListener('progress', onProgress);
            }
          }
        );
        directUpload.create((error, blob) => {
          if (error) reject(error);
          else resolve(blob.id);
        });
      });
    },
    retryUpload(item, index) {
      this.$delete(this.items, index);
      this.attachVideo(item.file);
    }
  }
};
